import {
  ChatContainer,
  MessageList,
  TypingIndicator,
  MessageInput,
  MainContainer,
} from '@chatscope/chat-ui-kit-react';
import {
  AiAssistantMessage,
  AiAssistantMessageRole,
  AiAssistantMessageType,
} from 'client/admin/core';
import { Btn, ButtonTypes } from 'client/shared/components/base';
import React from 'react';
import {
  AiAssistantMessageComponent,
  CustomAvatar,
  AvatarOverride,
} from '../../components/message';
import {
  CustomMessageInput,
  MessageInputWithCustomBtn,
} from '../../components/message-input';
import { LoadedAdminData } from 'client/admin/shared/types';
import { MessageInputDisableReason } from '../ai-assistant-container';
import {
  SUPPORT_EMAIL,
  AiAssistantName,
  assistantDetailsByName,
  AiAssistant,
} from 'core';
import { MAX_FILE_SIZE_MB } from '../../components/embedded-file-upload';
import { Box, Button, Typography } from '@mui/material';

export const COPY = {
  askAnotherQuestion: 'Ask another question',
  queryLimit: "You've reached the limit of questions for this month.",
  thinking: {
    [AiAssistantName.POLLY]: 'Polly is thinking',
    [AiAssistantName.GRANT_WRITER]: 'Grace is thinking',
    [AiAssistantName.ANNIE]: 'Annie is thinking',
    [AiAssistantName.SANDBOX]: 'Assistant is thinking',
  },
  cancelProcessing: 'Cancel Request',
  missingFips: (assistantName: AiAssistantName) =>
    `We\'re unable to load ${assistantDetailsByName[assistantName]}. Contact our support staff at ${SUPPORT_EMAIL} if the issue persists, we'll be happy to resolve it!`,
  fileUploadMessage: `Upload your data file to this conversation to utilize your document for more tailored responses. \n\n- ${MAX_FILE_SIZE_MB}MB max, per file\n\n- file types accepted: TXT, PDF, JSON, DOC, DOCX, PPTX \n\n<div></div>\n\n<EmbeddedContent type={file} data={""}/>`,
};

interface Props {
  readonly assistant: AiAssistant;
  readonly aiAssistantMessages: readonly AiAssistantMessage[];
  readonly adminData: LoadedAdminData;
  readonly isLoading: boolean;
  readonly requestInProcess: boolean;
  readonly events: {
    readonly onSend: (textContent: string, clearMessageContent: () => void) => void;
    readonly onCancelProcessing: () => void;
    readonly refreshSessionId: () => void;
  };
  readonly showCancelQuery: boolean;
  readonly isMessageInputDisabled: boolean;
  readonly showQueryLimit: boolean;
  readonly messageInputDisabledReason?: MessageInputDisableReason;
  readonly loadingText?: string;
  readonly queryLimit: number;
  readonly refreshAndRefetch: () => void;
  readonly messageInputEvents: {
    readonly setMessageContent: React.Dispatch<React.SetStateAction<string>>;
    readonly onSend: (onSendComplete?: () => void) => void;
  };
  readonly messageContent: string;
  readonly sessionId: string;
}

export const MainChatContainer: React.FC<Props> = (p) => {
  const [showFileUpload, setShowFileUpload] = React.useState(false);

  return (
    <MainContainer
      className={`bg-grayscale-2 px-4 rounded-bottom`}
      style={{ border: '1px solid #E0E0E0' }}
    >
      <ChatContainer>
        <MessageList
          className={`px-1 bg-grayscale-2`}
          disableOnYReachWhenNoScroll={true}
          loading={p.isLoading}
          typingIndicator={
            p.requestInProcess ? (
              <TypingIndicator
                className={`mx-1 align-items-center justify-content-center bg-transparent text-color-gray-60 text-center p-3`}
                content={
                  <div>
                    {p.loadingText ??
                      (p.assistant.type === 'StandardAssistant'
                        ? COPY.thinking[p.assistant.id]
                        : `Assistant is thinking`)}
                  </div>
                }
              />
            ) : null
          }
        >
          <MessageList.Content>
            {p.aiAssistantMessages.map((message, idx) =>
              AiAssistantMessageComponent({
                key: `${idx}`,
                adminData: p.adminData,
                aiAssistantMessage: message,
                assistant: p.assistant,
                className: `my-3`,
                sessionId: p.sessionId,
              })
            )}
            {showFileUpload &&
              AiAssistantMessageComponent({
                avatarOverride: AvatarOverride.UPLOAD,
                key: `${p.aiAssistantMessages.length}`,
                adminData: p.adminData,
                aiAssistantMessage: {
                  type: AiAssistantMessageType.SINGLE,
                  role: AiAssistantMessageRole.ASSISTANT,
                  content: COPY.fileUploadMessage,
                  timestamp: new Date(),
                },
                assistant: p.assistant,
                className: `my-3`,
                sessionId: p.sessionId,
              })}
            {p.requestInProcess && p.showCancelQuery ? (
              <div
                className={`d-flex pb-4 mb-4 justify-content-end align-items-center`}
              >
                <Btn
                  action={p.events.onCancelProcessing}
                  className={`text-color-jungle text-center px-3 py-2`}
                  type={ButtonTypes.SECONDARY}
                >
                  {COPY.cancelProcessing}
                </Btn>
                <div className={`invisible ml-3`}>
                  <CustomAvatar
                    adminData={p.adminData}
                    assistant={p.assistant}
                    role={AiAssistantMessageRole.ASSISTANT}
                  />
                </div>
              </div>
            ) : (
              <div className="pb-4 mb-4" />
            )}
          </MessageList.Content>
        </MessageList>
        <CustomMessageInput as={MessageInput}>
          {p.messageInputDisabledReason ? (
            getMessageInputDisabledButton({
              reason: p.messageInputDisabledReason,
              queryLimit: p.queryLimit,
              actions: {
                refresh: p.refreshAndRefetch,
              },
            })
          ) : (
            <MessageInputWithCustomBtn
              events={{
                ...p.messageInputEvents,
                onSend: () => {
                  p.messageInputEvents.onSend(() => setShowFileUpload(false));
                },
                onClickFileUpload: () => setShowFileUpload((prev) => !prev),
              }}
              isMessageInputDisabled={p.isLoading || p.isMessageInputDisabled}
              messageContent={p.messageContent}
            />
          )}
        </CustomMessageInput>
      </ChatContainer>
    </MainContainer>
  );
};
MainChatContainer.displayName = 'MainChatContainer';

function getMessageInputDisabledButton(args: {
  readonly reason: MessageInputDisableReason;
  readonly queryLimit: number;
  readonly actions: {
    readonly refresh: () => void;
  };
}) {
  const { reason, actions } = args;
  switch (reason) {
    case MessageInputDisableReason.EXPIRED_SESSION: {
      return (
        <Button
          onClick={actions.refresh}
          sx={{ width: '100%', p: 1, fontWeight: 'bold' }}
          variant="contained"
        >
          {COPY.askAnotherQuestion}
        </Button>
      );
    }
    case MessageInputDisableReason.QUERY_LIMIT_REACHED: {
      return (
        <Box
          borderRadius={1}
          p={1}
          sx={{
            bgcolor: 'primary.main',
          }}
          width="100%"
        >
          <Typography
            color="white"
            fontWeight={'bold'}
            textAlign="center"
            variant="body1"
          >
            {COPY.queryLimit}
          </Typography>
        </Box>
      );
    }
  }
}
